import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

export const ANIMATION_DURATION = 10;
export const ANIMATION_STEPS = 12;

const baseClassName = 'Cube';

// Modifier to keep perspective proportional to the size
const perspectiveMod = 4;

const cubeAnimation = keyframes`
    0% { transform: rotateY(0deg) rotateX(0deg) rotate(0deg); }
    8.3333% { transform: rotateY(0deg) rotateX(-90deg) rotate(0deg); }
    16.6666% { transform: rotateY(0deg) rotateX(-90deg) rotate(-90deg); }
    25% { transform: rotateY(0deg) rotateX(-180deg) rotate(-90deg); }
    33.3333% { transform: rotateY(-90deg) rotateX(-180deg) rotate(-90deg); }
    41.6666% { transform: rotateY(-90deg) rotateX(-180deg) rotate(-180deg); }
    50% { transform: rotateY(0deg) rotateX(-180deg) rotate(-180deg); }
    58.3333% { transform: rotateY(0deg) rotateX(-270deg) rotate(-180deg); }
    66.6666% { transform: rotateY(0deg) rotateX(-270deg) rotate(-90deg);  }
    75% { transform: rotateY(0deg) rotateX(-360deg) rotate(-90deg); }
    83.3333% { transform: rotateY(-90deg) rotateX(-360deg) rotate(-90deg); }
    91.6666% { transform: rotateY(-90deg) rotateX(-360deg) rotate(0deg); }
    100% { transform: rotateY(0deg) rotateX(-360deg) rotate(0deg); }
`;

const CubeContainer = styled.div`
    position: relative;
    height: ${(props) => props.size}px;
    width: ${(props) => props.size}px;
    perspective: ${(props) => props.size * perspectiveMod}px;
`;

const CubeBody = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
    transform-style: preserve-3d;
    animation: ${cubeAnimation} ${ANIMATION_DURATION}s ${(props) => props.animationDelay}s cubic-bezier(0.5, 0, 0.1, 1)
        infinite forwards;
`;

const CubeFace = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: ${(props) => props.color};
    background-image: url(${(props) => props.image});
    background-size: cover;
    background-repeat: no-repeat;
    transform: ${(props) => props.transform};
`;

const Cube = ({ animationDelay, className, colors, images, size }) => (
    <CubeContainer className={`${baseClassName}__container ${className}`} size={size}>
        <CubeBody className={baseClassName} animationDelay={animationDelay}>
            <CubeFace
                className={`${baseClassName}__face top`}
                transform={`rotateX(90deg) translateZ(${size / 2}px)`}
                color={colors.top || colors}
                image={images.top || images}
            />
            <CubeFace
                className={`${baseClassName}__face bottom`}
                transform={`rotateX(-90deg) translateZ(${size / 2}px)`}
                color={colors.bottom || colors}
                image={images.bottom || images}
            />
            <CubeFace
                className={`${baseClassName}__face left`}
                transform={`rotateY(-90deg) translateZ(${size / 2}px)`}
                color={colors.left || colors}
                image={images.left || images}
            />
            <CubeFace
                className={`${baseClassName}__face right`}
                transform={`rotateY(90deg) translateZ(${size / 2}px)`}
                color={colors.right || colors}
                image={images.right || images}
            />
            <CubeFace
                className={`${baseClassName}__face front`}
                transform={`rotateY(0deg) translateZ(${size / 2}px)`}
                color={colors.front || colors}
                image={images.front || images}
            />
            <CubeFace
                className={`${baseClassName}__face back`}
                transform={`rotateY(180deg) translateZ(${size / 2}px)`}
                color={colors.back || colors}
                image={images.back || images}
            />
        </CubeBody>
    </CubeContainer>
);

const cubeShape = PropTypes.shape({
    top: PropTypes.string,
    bottom: PropTypes.string,
    left: PropTypes.string,
    right: PropTypes.string,
    front: PropTypes.string,
    back: PropTypes.string,
});

Cube.propTypes = {
    // [integer] Specifies the animation delay of the cube
    animationDelay: PropTypes.number,
    // [string] Additional classes to be added to the .Cube__container div
    className: PropTypes.string,
    // colors and images can accept a string to be applied to all sides, or an object
    // that specifies strings for each side of the cube. (See cubeShape above)
    // [string/object] Specify the background-color of each cube face
    colors: PropTypes.oneOfType([cubeShape, PropTypes.string]).isRequired,
    // [string/object] Specify the background-image (url) of each cube face
    images: PropTypes.oneOfType([cubeShape, PropTypes.string]).isRequired,
    // [integer] Determines the height and width of the cube
    size: PropTypes.number.isRequired,
};

Cube.defaultProps = {
    animationDelay: 0,
    className: '',
};

export default Cube;
